import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getContact, getContactDevices, getContactTickets,
  analyzeContact, updateDevice, getClient
} from "../api";
import RemoteButton from "../components/RemoteButton";
import { tokens, sx, FullPageSpinner, MiniAvatar } from "../theme";
import useIsMobile from "../hooks/useIsMobile";

const API   = "/api";
const token = () => localStorage.getItem("midtc_token") || "";

// ── Helpers ────────────────────────────────────────────────────────────────
const scoreColor    = (s) => s >= 80 ? "#22c55e" : s >= 60 ? "#f59e0b" : "#ef4444";
const scoreLabel    = (s) => s >= 80 ? "Healthy"  : s >= 60 ? "Warning"  : "Critical";
const slaColor      = (s) => ({ ok:"#22c55e", breach:"#ef4444", hold:"#f59e0b" }[s] || "#6b7280");
const slaLabel      = (s) => ({ ok:"Within SLA", breach:"SLA Breached", hold:"SLA Hold" }[s] || s);
const statusColor   = (s) => ({ Open:"#3b82f6","In Progress":"#8b5cf6","On Hold":"#f59e0b",Resolved:"#22c55e",Closed:"#6b7280" }[s] || "#6b7280");
const priorityColor = (p) => ({ Critical:"#ef4444",High:"#f97316",Medium:"#f59e0b",Low:"#22c55e" }[p] || "#6b7280");
const fmt     = (d) => d ? new Date(d).toLocaleDateString()  : "—";
const fmtTime = (d) => d ? new Date(d).toLocaleString()      : "Never";

// ── Shared Styles (sourced from theme) ────────────────────────────────────
const S = {
  page:    { minHeight:"100vh", background:`radial-gradient(circle at 80% -10%, rgba(124,58,237,0.05), transparent 50%), ${tokens.surface.page}`, color:tokens.text.primary, padding:"24px", fontFamily:"'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
  back:    { background:"none", border:"none", color:tokens.text.faint, cursor:"pointer", fontSize:13, marginBottom:14, display:"flex", alignItems:"center", gap:6, fontFamily:"inherit" },
  header:  { paddingBottom:14, borderBottom:`1px solid ${tokens.border.soft}`, marginBottom:18, display:"flex", justifyContent:"space-between", alignItems:"flex-end", flexWrap:"wrap", gap:12 },
  avatar:  { width:48, height:48, borderRadius:"50%", background:tokens.gradient.primary, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:800, color:"#fff", flexShrink:0, boxShadow:"0 0 16px rgba(99,102,241,0.4), inset 0 1px 0 rgba(255,255,255,0.15)" },
  title:   { fontSize:22, fontWeight:800, color:tokens.text.primary, margin:0, letterSpacing:"-0.5px" },
  sub:     { fontSize:13, color:tokens.text.faint, marginTop:3 },
  grid:    { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:10, marginBottom:18 },
  stat:    { background:tokens.surface.panel, border:`1px solid ${tokens.border.soft}`, borderRadius:tokens.radius.md, padding:"15px 16px", position:"relative", overflow:"hidden", boxShadow:tokens.shadow.panel },
  statVal: { fontSize:26, fontWeight:800, color:tokens.text.primary, letterSpacing:-1, lineHeight:1 },
  statLbl: { fontSize:10, color:tokens.text.label, marginTop:6, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em" },
  card:    { background:tokens.surface.panel, border:`1px solid ${tokens.border.soft}`, borderRadius:tokens.radius.md, padding:20, marginBottom:14, boxShadow:tokens.shadow.panel },
  cardH:   { fontSize:13, fontWeight:700, color:tokens.text.body, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" },
  table:   { width:"100%", borderCollapse:"collapse" },
  th:      { textAlign:"left", padding:"10px 12px", fontSize:10, color:tokens.text.faint, borderBottom:`1px solid ${tokens.border.soft}`, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em" },
  td:      { padding:"11px 12px", fontSize:13, color:tokens.text.body, borderBottom:`1px solid ${tokens.border.softer}` },
  badge:   (c) => ({ display:"inline-flex", alignItems:"center", padding:"3px 9px", borderRadius:99, fontSize:11, fontWeight:700, background:c+"22", color:c, border:`1px solid ${c}40` }),
  btn:     (c) => c
    ? { background:c, color:"#fff", border:"none", borderRadius:7, padding:"7px 14px", fontSize:12, cursor:"pointer", fontWeight:700, fontFamily:"inherit" }
    : { ...sx.buttonPrimary, padding:"7px 14px", fontSize:12 },
  score:   (s) => ({ display:"inline-flex", alignItems:"center", justifyContent:"center", width:42, height:42, borderRadius:"50%", fontSize:14, fontWeight:800, background:scoreColor(s)+"22", color:scoreColor(s), border:`1px solid ${scoreColor(s)}40`, boxShadow:`0 0 12px ${scoreColor(s)}33` }),
  alert:   { background:"rgba(127,29,29,0.3)", border:"1px solid rgba(239,68,68,0.4)", borderRadius:8, padding:"7px 11px", fontSize:12, color:tokens.accent.red2, marginBottom:6 },
  ai:      { background:"linear-gradient(135deg,rgba(124,58,237,0.12),rgba(99,102,241,0.06))", border:"1px solid rgba(124,58,237,0.3)", borderRadius:tokens.radius.md, padding:16, fontSize:13, color:"var(--t-text-body)", lineHeight:1.7, whiteSpace:"pre-wrap", boxShadow:"0 0 18px rgba(124,58,237,0.1)" },
  spinner: { width:20, height:20, border:`2px solid ${tokens.accent.indigo}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite", display:"inline-block" },
  section:      { background:tokens.surface.panel, border:`1px solid ${tokens.border.soft}`, borderRadius:tokens.radius.md, padding:20, marginBottom:14, boxShadow:tokens.shadow.panel },
  sectionTitle: { fontSize:11, fontWeight:700, color:tokens.text.label, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:14, display:"flex", alignItems:"center", gap:8 },
  input:        { ...sx.input, padding:"9px 12px", fontSize:14 },
  label:        { fontSize:11, color:tokens.text.label, display:"block", marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" },
  btnBlue:      { ...sx.buttonPrimary, padding:"9px 18px" },
  btnRed:       { background:"rgba(220,38,38,0.15)", color:tokens.accent.red, border:"1px solid rgba(220,38,38,0.35)", padding:"9px 18px", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6, fontFamily:"inherit" },
  btnGhost:     { ...sx.buttonGhost, padding:"9px 18px", fontWeight:600 },
  toggle:       (on) => ({ width:42, height:24, borderRadius:12, cursor:"pointer", background: on ? tokens.gradient.primary : tokens.surface.input, border:`1px solid ${on ? "rgba(99,102,241,0.5)" : tokens.border.mid}`, position:"relative", transition:"all .2s", flexShrink:0, padding:0, boxShadow: on ? "0 0 8px rgba(99,102,241,0.4)" : "none" }),
  toggleDot:    (on) => ({ position:"absolute", top:2, left: on ? 21 : 2, width:18, height:18, borderRadius:"50%", background:"#fff", transition:"left .2s", pointerEvents:"none", boxShadow:"0 1px 3px rgba(0,0,0,0.3)" }),
  settingsBadge:(c) => ({ background:c+"22", color:c, border:`1px solid ${c}44`, borderRadius:999, padding:"3px 10px", fontSize:11, fontWeight:700 }),
};

// ═══════════════════════════════════════════════════════════════════════════
//  SETTINGS SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function ResultBanner({ result, onDismiss }) {
  if (!result) return null;
  const ok = result.type === "success";
  return (
    <div style={{
      background: ok ? "rgba(22,163,74,0.1)"  : "rgba(220,38,38,0.1)",
      border:     ok ? "1px solid rgba(22,163,74,0.3)" : "1px solid rgba(220,38,38,0.3)",
      borderRadius:8, padding:"14px 16px", marginTop:14,
      display:"flex", alignItems:"flex-start", gap:10,
    }}>
      <span style={{ fontSize:18 }}>{ok ? "✅" : "❌"}</span>
      <div style={{ flex:1 }}>
        <div style={{ fontWeight:700, color:ok?"#4ade80":"#f87171", fontSize:14 }}>
          {ok ? "Operation Completed" : "Operation Failed"}
        </div>
        <div style={{ color:"var(--t-text-muted)", fontSize:13, marginTop:3, lineHeight:1.5 }}>
          {result.message}
        </div>
        {result.details && (
          <div style={{ background: "var(--t-panel)", border:"1px solid var(--t-border-strong)", borderRadius:6, padding:"8px 10px", marginTop:8, fontFamily:"monospace", fontSize:12, color:"var(--t-text-label)", whiteSpace:"pre-wrap" }}>
            {result.details}
          </div>
        )}
      </div>
      <button onClick={onDismiss} style={{ background:"none", border:"none", color:"var(--t-text-deep)", cursor:"pointer", fontSize:16, lineHeight:1, padding:0 }}>×</button>
    </div>
  );
}

function ConfirmDialog({ message, subtext, onConfirm, onCancel, confirmLabel="Confirm", danger=false }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:12, padding:28, maxWidth:420, width:"100%", boxShadow:"0 24px 64px rgba(0,0,0,0.5)" }}>
        <div style={{ fontSize:32, marginBottom:12, textAlign:"center" }}>{danger ? "⚠️" : "❓"}</div>
        <h3 style={{ margin:"0 0 8px", color:"var(--t-text-primary)", fontSize:16, textAlign:"center" }}>{message}</h3>
        {subtext && <p style={{ color:"var(--t-text-label)", fontSize:13, textAlign:"center", margin:"0 0 20px", lineHeight:1.5 }}>{subtext}</p>}
        <div style={{ display:"flex", gap:10, marginTop:20 }}>
          <button onClick={onCancel}  style={{ ...S.btnGhost, flex:1 }}>Cancel</button>
          <button onClick={onConfirm} style={{ ...(danger ? S.btnRed : S.btnBlue), flex:1, justifyContent:"center" }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Settings Tab ───────────────────────────────────────────────────────────
// clientDomain is fetched from the parent by loading the client record,
// since GET /api/contacts/:id does not return client_domain.
function ContactSettingsTab({ contact, onUpdate, clientDomain }) {
  // Portal Access
  const [portalEnabled, setPortalEnabled] = useState(!!contact.portal_access);
  const [portalRole,    setPortalRole]    = useState(contact.portal_role || "user");
  const [portalSaving,  setPortalSaving]  = useState(false);
  const [portalResult,  setPortalResult]  = useState(null);

  // Local Password Reset
  const [pwResetting,  setPwResetting]  = useState(false);
  const [pwResult,     setPwResult]     = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  // O365 Password Reset
  const [o365Loading,      setO365Loading]      = useState(false);
  const [o365Result,       setO365Result]       = useState(null);
  const [confirmO365Reset, setConfirmO365Reset] = useState(false);
  const [newTempPassword,  setNewTempPassword]  = useState("");
  const [o365Mode,         setO365Mode]         = useState("generate");

  // Notifications
  const [notifs, setNotifs] = useState({
    ticket_created:    contact.notify_ticket_created  ?? true,
    ticket_updated:    contact.notify_ticket_updated  ?? true,
    ticket_resolved:   contact.notify_ticket_resolved ?? true,
    ticket_sla_breach: contact.notify_sla_breach      ?? false,
  });
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifResult, setNotifResult] = useState(null);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const savePortalSettings = async () => {
    setPortalSaving(true); setPortalResult(null);
    try {
      const res  = await fetch(`${API}/contacts/${contact.id}/portal-settings`, {
        method:"PATCH",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:JSON.stringify({ portal_access:portalEnabled, portal_role:portalRole }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save portal settings.");
      setPortalResult({ type:"success", message:`Portal access ${portalEnabled?"enabled":"disabled"} with role: ${portalRole}.` });
      if (onUpdate) onUpdate();
    } catch (err) {
      setPortalResult({ type:"error", message:err.message });
    } finally { setPortalSaving(false); }
  };

  const sendPasswordReset = async () => {
    setConfirmReset(false); setPwResetting(true); setPwResult(null);
    try {
      const res  = await fetch(`${API}/contacts/${contact.id}/send-password-reset`, {
        method:"POST",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send reset email.");
      setPwResult({
        type:"success",
        message:`Password reset email sent to ${contact.email}.`,
        details:`Reset link expires in: 24 hours\nSent at: ${new Date().toLocaleString()}`,
      });
    } catch (err) { setPwResult({ type:"error", message:err.message }); }
    finally { setPwResetting(false); }
  };

  const runO365PasswordReset = async () => {
    setConfirmO365Reset(false); setO365Loading(true); setO365Result(null);
    try {
      const body = { contact_id:contact.id, email:contact.email, mode:o365Mode };
      if (o365Mode === "custom" && newTempPassword) body.temp_password = newTempPassword;
      const res  = await fetch(`${API}/contacts/${contact.id}/o365-reset-password`, {
        method:"POST",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "O365 password reset failed.");
      setO365Result({
        type:"success",
        message:`Microsoft 365 password has been reset for ${contact.email}.`,
        details:[
          `Tenant: ${data.tenant || clientDomain || "N/A"}`,
          `UPN: ${data.userPrincipalName || contact.email}`,
          `Temp password: ${data.temporaryPassword || (o365Mode==="custom" ? newTempPassword : "[auto-generated]")}`,
          `Must change on next login: ${data.forceChangePasswordNextSignIn ? "Yes" : "No"}`,
          `Completed at: ${new Date().toLocaleString()}`,
        ].join("\n"),
      });
    } catch (err) {
      setO365Result({
        type:"error", message:err.message,
        details:"Check that this contact's email matches a licensed user in the client's M365 tenant and that the Azure Tenant ID is configured on the Client page. Requires UserAuthenticationMethod.ReadWrite.All permission.",
      });
    } finally { setO365Loading(false); }
  };

  const saveNotifications = async () => {
    setNotifSaving(true); setNotifResult(null);
    try {
      const res  = await fetch(`${API}/contacts/${contact.id}/notifications`, {
        method:"PATCH",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:JSON.stringify(notifs),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save notification settings.");
      setNotifResult({ type:"success", message:"Notification preferences saved." });
      if (onUpdate) onUpdate();
    } catch (err) { setNotifResult({ type:"error", message:err.message }); }
    finally { setNotifSaving(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingTop:4 }}>

      {/* ── 1. PORTAL ACCESS ──────────────────────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>🔐 Portal Access</div>

        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16 }}>
          <button style={S.toggle(portalEnabled)} onClick={() => setPortalEnabled(v => !v)}>
            <div style={S.toggleDot(portalEnabled)} />
          </button>
          <div>
            <div style={{ fontSize:14, fontWeight:600, color:"var(--t-text-primary)" }}>
              {portalEnabled ? "Portal Access Enabled" : "Portal Access Disabled"}
            </div>
            <div style={{ fontSize:12, color:"var(--t-text-label)" }}>
              {portalEnabled
                ? "This contact can log into the client portal."
                : "This contact cannot log into the client portal."}
            </div>
          </div>
        </div>

        {portalEnabled && (
          <div style={{ marginBottom:16 }}>
            <label style={S.label}>Portal Role</label>
            <div style={{ display:"flex", gap:8 }}>
              {["user","admin"].map(role => (
                <button key={role} onClick={() => setPortalRole(role)} style={{
                  padding:"8px 20px", borderRadius:7, fontWeight:600, fontSize:13,
                  cursor:"pointer", textTransform:"capitalize",
                  border:    portalRole===role ? "none" : "1px solid #334155",
                  background:portalRole===role ? (role==="admin" ? "rgba(234,179,8,0.2)" : "rgba(29,78,216,0.2)") : "#1e293b",
                  color:     portalRole===role ? (role==="admin" ? "#eab308" : "#60a5fa") : "#64748b",
                }}>
                  {role === "admin" ? "👑 Admin" : "👤 User"}
                </button>
              ))}
            </div>
            <div style={{ fontSize:12, color:"var(--t-text-deep)", marginTop:6 }}>
              {portalRole === "admin"
                ? "Admin contacts can view all tickets and contacts for their company."
                : "User contacts can only view and submit their own tickets."}
            </div>
          </div>
        )}

        <button onClick={savePortalSettings} disabled={portalSaving} style={S.btnBlue}>
          {portalSaving ? "Saving…" : "💾 Save Portal Settings"}
        </button>
        <ResultBanner result={portalResult} onDismiss={() => setPortalResult(null)} />
      </div>

      {/* ── 2. PORTAL PASSWORD RESET ──────────────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>📧 Portal Password Reset</div>
        <p style={{ margin:"0 0 14px", fontSize:13, color:"var(--t-text-label)", lineHeight:1.6 }}>
          Send a password reset email to{" "}
          <strong style={{ color:"var(--t-text-primary)" }}>{contact.email}</strong>.
          The link will expire after 24 hours.
        </p>
        <button
          onClick={() => setConfirmReset(true)}
          disabled={pwResetting || !portalEnabled}
          style={{ ...S.btnBlue, opacity:!portalEnabled?0.5:1 }}
          title={!portalEnabled ? "Enable portal access first" : ""}
        >
          {pwResetting ? "Sending…" : "📤 Send Password Reset Email"}
        </button>
        {!portalEnabled && (
          <div style={{ fontSize:12, color:"#f59e0b", marginTop:8 }}>
            ⚠ Enable portal access above before sending a reset email.
          </div>
        )}
        <ResultBanner result={pwResult} onDismiss={() => setPwResult(null)} />
      </div>

      {/* ── 3. MICROSOFT 365 PASSWORD RESET ───────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>
          <svg width="14" height="14" viewBox="0 0 21 21" fill="none" style={{ flexShrink:0 }}>
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          Microsoft 365 Password Reset
        </div>

        <p style={{ margin:"0 0 14px", fontSize:13, color:"var(--t-text-label)", lineHeight:1.6 }}>
          Reset this user's password directly in their Microsoft 365 tenant via the Graph API.
          Requires <strong style={{ color:"var(--t-text-muted)" }}>UserAuthenticationMethod.ReadWrite.All</strong> permission.
        </p>

        {/* Tenant info row — now uses clientDomain fetched from client record */}
        <div style={{ background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:8, padding:"10px 14px", marginBottom:14, display:"flex", alignItems:"center", gap:10, fontSize:13 }}>
          <span style={{ color:"var(--t-text-label)" }}>Client domain:</span>
          <span style={{ color:"var(--t-text-primary)", fontWeight:600 }}>
            {clientDomain || "No domain configured"}
          </span>
          {clientDomain
            ? <span style={S.settingsBadge("#4ade80")}>✓ Linked</span>
            : <span style={S.settingsBadge("#f59e0b")}>⚠ No Domain</span>}
        </div>

        {/* Mode toggle */}
        <div style={{ marginBottom:14 }}>
          <label style={S.label}>Password Mode</label>
          <div style={{ display:"flex", gap:8 }}>
            {[{ val:"generate", label:"🎲 Auto-Generate" },{ val:"custom", label:"✏️ Set Custom" }].map(m => (
              <button key={m.val} onClick={() => setO365Mode(m.val)} style={{
                padding:"7px 16px", borderRadius:7, fontSize:13, fontWeight:600, cursor:"pointer",
                background: o365Mode===m.val ? "rgba(29,78,216,0.2)" : "#1e293b",
                color:      o365Mode===m.val ? "#60a5fa" : "#64748b",
                border:     o365Mode===m.val ? "1px solid rgba(29,78,216,0.4)" : "1px solid #334155",
              }}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {o365Mode === "custom" && (
          <div style={{ marginBottom:14 }}>
            <label style={S.label}>Temporary Password</label>
            <input
              type="text"
              placeholder="Min 8 chars, uppercase, number & symbol"
              value={newTempPassword}
              onChange={e => setNewTempPassword(e.target.value)}
              style={S.input}
            />
          </div>
        )}

        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button
            onClick={() => setConfirmO365Reset(true)}
            disabled={o365Loading || !clientDomain || (o365Mode==="custom" && !newTempPassword)}
            style={{
              ...S.btnBlue,
              background:"linear-gradient(135deg,#4f6fff,#0ea5e9)",
              opacity:(!clientDomain || (o365Mode==="custom" && !newTempPassword)) ? 0.5 : 1,
            }}
          >
            {o365Loading ? (
              <><span style={{ display:"inline-block", animation:"spin 1s linear infinite" }}>⟳</span> Resetting…</>
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Reset M365 Password
              </>
            )}
          </button>
          {o365Loading && <span style={{ fontSize:13, color:"var(--t-text-label)" }}>Connecting to Microsoft Graph API…</span>}
        </div>

        {!clientDomain && (
          <div style={{ fontSize:12, color:"#f59e0b", marginTop:8 }}>
            ⚠ No domain found for this client. Set the client domain on the Client settings page first.
          </div>
        )}

        <ResultBanner result={o365Result} onDismiss={() => setO365Result(null)} />
      </div>

      {/* ── 4. NOTIFICATION PREFERENCES ───────────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>🔔 Email Notification Preferences</div>

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[
            { key:"ticket_created",    label:"New ticket submitted",        sub:"Notified when a ticket is opened under their account." },
            { key:"ticket_updated",    label:"Ticket updated / replied to", sub:"Notified when an agent responds or updates a ticket." },
            { key:"ticket_resolved",   label:"Ticket resolved or closed",   sub:"Notified when their ticket is marked resolved." },
            { key:"ticket_sla_breach", label:"SLA breach alerts",           sub:"Notified when a ticket is approaching or has breached SLA." },
          ].map(n => (
            <div key={n.key} style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 14px", background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:8 }}>
              <button style={S.toggle(notifs[n.key])} onClick={() => setNotifs(p => ({ ...p, [n.key]:!p[n.key] }))}>
                <div style={S.toggleDot(notifs[n.key])} />
              </button>
              <div>
                <div style={{ fontSize:14, fontWeight:600, color:"var(--t-text-primary)" }}>{n.label}</div>
                <div style={{ fontSize:12, color:"var(--t-text-label)" }}>{n.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={saveNotifications} disabled={notifSaving} style={{ ...S.btnBlue, marginTop:14 }}>
          {notifSaving ? "Saving…" : "💾 Save Notification Settings"}
        </button>
        <ResultBanner result={notifResult} onDismiss={() => setNotifResult(null)} />
      </div>

      {/* ── CONFIRM DIALOGS ────────────────────────────────────────────────── */}
      {confirmReset && (
        <ConfirmDialog
          message="Send Password Reset Email?"
          subtext={`A reset link will be emailed to ${contact.email}. It expires in 24 hours.`}
          confirmLabel="Send Reset Email"
          onConfirm={sendPasswordReset}
          onCancel={() => setConfirmReset(false)}
        />
      )}
      {confirmO365Reset && (
        <ConfirmDialog
          message="Reset Microsoft 365 Password?"
          subtext={`This will immediately change ${contact.email}'s M365 password and force a change on next login. This cannot be undone.`}
          confirmLabel="Reset M365 Password"
          danger
          onConfirm={runO365PasswordReset}
          onCancel={() => setConfirmO365Reset(false)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function Contact360() {
  const { id }         = useParams();
  const navigate       = useNavigate();
  const isMobile       = useIsMobile();
  const [contact,      setContact]      = useState(null);
  const [devices,      setDevices]      = useState([]);
  const [tickets,      setTickets]      = useState([]);
  const [analysis,     setAnalysis]     = useState("");
  const [loading,      setLoading]      = useState(true);
  const [analyzing,    setAnalyzing]    = useState(false);
  const [tab,          setTab]          = useState("devices");
  // clientDomain fetched separately — GET /api/contacts/:id does NOT return
  // the parent client's domain, so we fetch the client record explicitly.
  const [clientDomain, setClientDomain] = useState("");

  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    console.log(`[Contact360] load() starting, id=${id}`);
    try {
      setLoading(true);
      setLoadError(null);
      const t0 = Date.now();
      const c = await getContact(id);
      console.log(`[Contact360] getContact done in ${Date.now()-t0}ms`, c?.data);
      setContact(c.data);
      const t1 = Date.now();
      const [d, t] = await Promise.allSettled([
        getContactDevices(id), getContactTickets(id),
      ]);
      console.log(`[Contact360] devices+tickets done in ${Date.now()-t1}ms`, { d: d.status, t: t.status });
      setDevices(d.status === "fulfilled" ? (d.value.data || []) : []);
      setTickets(t.status === "fulfilled" ? (t.value.data || []) : []);
      if (c.data?.client_id) {
        try {
          const cl = await getClient(c.data.client_id);
          setClientDomain(cl.data?.domain || "");
        } catch {
          setClientDomain("");
        }
      }
    } catch (err) {
      console.error("[Contact360] load failed:", err);
      setLoadError(err?.response?.data?.error || err?.message || "Failed to load contact");
    } finally {
      console.log(`[Contact360] load() finished, setting loading=false`);
      setLoading(false);
    }
  }, [id]);

  // Run on mount only — polling was masking errors and re-triggering loading=true
  useEffect(() => {
    load();
  }, [load]);

  // Visible loading countdown so we can see React is actually running
  const [waitedMs, setWaitedMs] = useState(0);
  useEffect(() => {
    if (!loading) { setWaitedMs(0); return; }
    const t0 = Date.now();
    const interval = setInterval(() => setWaitedMs(Date.now() - t0), 100);
    return () => clearInterval(interval);
  }, [loading]);

  // Hard stop: if we're still loading after 8s, force-exit with an error
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      setLoadError("Request timed out after 8 seconds. Check the browser console for details.");
      setLoading(false);
    }, 8000);
    return () => clearTimeout(t);
  }, [loading]);

  const handleAnalyze = async () => {
    setAnalyzing(true); setAnalysis("");
    try {
      const res = await analyzeContact(id);
      setAnalysis(res.data.analysis);
    } catch {
      setAnalysis("Analysis failed. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) return (
    <div style={{ ...S.page, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12 }}>
      <div style={{ width:36, height:36, border:`3px solid ${tokens.accent.indigo}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
      <div style={{ fontSize:12, color:tokens.text.faint, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em" }}>
        Loading contact… {(waitedMs/1000).toFixed(1)}s
      </div>
      <div style={{ fontSize:10, color:tokens.text.deep }}>id: {id}</div>
    </div>
  );

  if (!contact) return (
    <div style={{ ...S.page, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ ...S.card, padding:32, textAlign:"center", maxWidth:420 }}>
        <div style={{ fontSize:32, marginBottom:8 }}>👤</div>
        <h2 style={{ fontSize:18, fontWeight:800, color:tokens.text.primary, marginBottom:8, letterSpacing:"-0.3px" }}>Contact not found</h2>
        <p style={{ fontSize:13, color:tokens.text.faint, lineHeight:1.6, marginBottom:16 }}>
          {loadError || "The contact you’re looking for couldn’t be loaded. They may have been deleted or you don’t have access."}
        </p>
        <button onClick={() => navigate("/clients")} style={sx.buttonPrimary}>
          ← Back to Clients
        </button>
      </div>
    </div>
  );

  const initials    = `${contact.first_name?.[0]||""}${contact.last_name?.[0]||""}`.toUpperCase();
  const alerts      = devices.flatMap(d => (d.alerts||[]).map(a => ({ device:d.device_name, msg:a })));
  const critDevices = devices.filter(d => d.health_score !== null && d.health_score < 60);

  const TABS = [
    { key:"devices",  label:`Devices (${devices.length})`  },
    { key:"tickets",  label:`Tickets (${tickets.length})`  },
    { key:"settings", label:"⚙️ Settings"                  },
  ];

  return (
    <div style={{ ...S.page, ...(isMobile ? { padding: 14 } : {}) }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Back */}
      <button style={S.back} onClick={() => navigate(`/clients/${contact.client_id}`)}>
        ← Back to {contact.company_name}
      </button>

      {/* Header */}
      <div style={S.header}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={S.avatar}>{initials}</div>
          <div>
            <h1 style={S.title}>{contact.first_name} {contact.last_name}</h1>
            <p style={S.sub}>
              {contact.job_title && <span>{contact.job_title} · </span>}
              <span>{contact.company_name}</span>
              {contact.email && <span> · {contact.email}</span>}
              {contact.phone && <span> · {contact.phone}</span>}
            </p>
          </div>
        </div>
        <button style={S.btn("#7b61ff")} onClick={handleAnalyze} disabled={analyzing}>
          {analyzing ? <span style={S.spinner} /> : "🤖 AI Analysis"}
        </button>
      </div>

      {/* Stat cards */}
      <div style={S.grid}>
        <div style={S.stat}>
          <div style={S.statVal}>{contact.open_tickets}</div>
          <div style={S.statLbl}>Open Tickets</div>
        </div>
        <div style={S.stat}>
          <div style={S.statVal}>{contact.total_tickets}</div>
          <div style={S.statLbl}>Total Tickets</div>
        </div>
        <div style={S.stat}>
          <div style={S.statVal}>{contact.device_count}</div>
          <div style={S.statLbl}>Devices</div>
        </div>
        <div style={S.stat}>
          <div style={{ ...S.statVal, color:critDevices.length>0?"#ef4444":"#22c55e" }}>
            {critDevices.length}
          </div>
          <div style={S.statLbl}>Critical Devices</div>
        </div>
        <div style={S.stat}>
          <div style={{ ...S.statVal, color:alerts.length>0?"#f59e0b":"#22c55e" }}>
            {alerts.length}
          </div>
          <div style={S.statLbl}>Active Alerts</div>
        </div>
      </div>

      {/* Device alerts */}
      {alerts.length > 0 && (
        <div style={S.card}>
          <div style={S.cardH}>⚠️ Device Alerts</div>
          {alerts.map((a,i) => (
            <div key={i} style={S.alert}><strong>{a.device}:</strong> {a.msg}</div>
          ))}
        </div>
      )}

      {/* AI Analysis */}
      {(analysis || analyzing) && (
        <div style={S.card}>
          <div style={S.cardH}>🤖 Claude AI Analysis</div>
          {analyzing
            ? <div style={{ display:"flex", alignItems:"center", gap:10, color:"#6b7280" }}><div style={S.spinner} /> Analyzing...</div>
            : <div style={S.ai}>{analysis}</div>
          }
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:isMobile?"wrap":"nowrap" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            ...S.btn(tab===t.key ? "#4f6fff" : "#1e293b"),
            ...(t.key==="settings" && tab!==t.key ? { border:"1px solid var(--t-border-strong)" } : {}),
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Devices Tab ───────────────────────────────────────────────────── */}
      {tab === "devices" && (
        <div style={S.card}>
          <div style={S.cardH}>Assigned Devices</div>
          {devices.length === 0
            ? <p style={{ color:"#6b7280", fontSize:13 }}>No devices assigned to this user.</p>
            : (
              <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {["Device","OS","Health","CPU","RAM","Disk","AV","Updates","Last Seen",""].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {devices.map(d => (
                    <tr key={d.device_id}>
                      <td style={S.td}>
                        <div style={{ fontWeight:500 }}>{d.device_name}</div>
                        <div style={{ fontSize:11, color:"#6b7280" }}>{d.hostname}</div>
                      </td>
                      <td style={S.td}>
                        <div style={{ fontSize:12 }}>{d.os}</div>
                        <div style={{ fontSize:11, color:"#6b7280" }}>{d.os_version}</div>
                      </td>
                      <td style={S.td}>
                        {d.health_score !== null ? (
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={S.score(d.health_score)}>{d.health_score}</div>
                            <span style={{ fontSize:11, color:scoreColor(d.health_score) }}>{scoreLabel(d.health_score)}</span>
                          </div>
                        ) : <span style={{ color:"#6b7280" }}>—</span>}
                      </td>
                      <td style={S.td}>{d.cpu_load_percent  != null ? `${d.cpu_load_percent}%`  : "—"}</td>
                      <td style={S.td}>{d.ram_used_percent  != null ? `${d.ram_used_percent}%`  : "—"}</td>
                      <td style={S.td}>
                        <span style={{ color:d.disk_used_percent>90?"#ef4444":d.disk_used_percent>80?"#f59e0b":"#e2e8f0" }}>
                          {d.disk_used_percent != null ? `${d.disk_used_percent}%` : "—"}
                        </span>
                      </td>
                      <td style={S.td}>
                        <span style={S.badge(d.av_status==="enabled"?"#22c55e":"#ef4444")}>
                          {d.av_status || "—"}
                        </span>
                      </td>
                      <td style={S.td}>
                        <span style={{ color:d.pending_updates>10?"#f59e0b":"#e2e8f0" }}>
                          {d.pending_updates ?? "—"}
                        </span>
                      </td>
                      <td style={S.td} title={fmtTime(d.last_seen)}>{fmt(d.last_seen)}</td>
                      <td style={S.td}>
                        <RemoteButton
                          deviceId={d.device_id}
                          deviceName={d.device_name}
                          contactName={`${contact.first_name} ${contact.last_name}`}
                          size="sm"
                          variant="outline"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )
          }
        </div>
      )}

      {/* ── Tickets Tab ───────────────────────────────────────────────────── */}
      {tab === "tickets" && (
        <div style={S.card}>
          <div style={S.cardH}>
            Tickets
            <Link to={`/tickets?contact_id=${id}`} style={{ fontSize:12, color:"#3b82f6", textDecoration:"none" }}>
              View all →
            </Link>
          </div>
          {tickets.length === 0
            ? <p style={{ color:"#6b7280", fontSize:13 }}>No tickets found.</p>
            : (
              <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {["Ticket","Subject","Status","Priority","SLA","Assigned To","Date"].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tickets.map(t => (
                    <tr key={t.id} style={{ cursor:"pointer" }} onClick={() => navigate(`/tickets/${t.id}`)}>
                      <td style={S.td}><span style={{ color:"#3b82f6", fontWeight:500 }}>{t.ticket_number}</span></td>
                      <td style={{ ...S.td, maxWidth:220 }}>
                        <div style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.subject}</div>
                        <div style={{ fontSize:11, color:"#6b7280" }}>{t.category}</div>
                      </td>
                      <td style={S.td}><span style={S.badge(statusColor(t.status))}>{t.status}</span></td>
                      <td style={S.td}><span style={S.badge(priorityColor(t.priority))}>{t.priority}</span></td>
                      <td style={S.td}><span style={S.badge(slaColor(t.sla_status))}>{slaLabel(t.sla_status)}</span></td>
                      <td style={S.td}>{t.assigned_to_name || "—"}</td>
                      <td style={S.td}>{fmt(t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )
          }
        </div>
      )}

      {/* ── Settings Tab ──────────────────────────────────────────────────── */}
      {tab === "settings" && (
        <ContactSettingsTab
          contact={contact}
          onUpdate={load}
          clientDomain={clientDomain}
        />
      )}
    </div>
  );
}
